import { useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';

// third-party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import { useChangeMyPasswordMutation } from './authApi';

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Ingresa tu contraseña actual'),
  newPassword: Yup.string().min(8, 'Debe tener al menos 8 caracteres').required('Ingresa la nueva contraseña'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden')
    .required('Confirma la nueva contraseña')
});

export default function ChangePasswordDialog({ open, onClose }) {
  const [changeMyPassword] = useChangeMyPasswordMutation();
  const [success, setSuccess] = useState(false);

  const handleClose = (formikReset) => {
    setSuccess(false);
    formikReset?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={() => handleClose()} maxWidth="xs" fullWidth>
      <Formik
        initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '', submit: null }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setErrors, setSubmitting, resetForm }) => {
          try {
            await changeMyPassword({ currentPassword: values.currentPassword, newPassword: values.newPassword }).unwrap();
            setSuccess(true);
            setSubmitting(false);
            resetForm();
          } catch (error) {
            setErrors({ submit: error.data?.message ?? 'No se pudo cambiar la contraseña.' });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, resetForm, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <DialogContent>
              <Stack sx={{ gap: 2, mt: 0.5 }}>
                {success && <Alert severity="success">Contraseña actualizada correctamente.</Alert>}
                {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="currentPassword">Contraseña actual</InputLabel>
                  <OutlinedInput
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    fullWidth
                    value={values.currentPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.currentPassword && errors.currentPassword)}
                  />
                  {touched.currentPassword && errors.currentPassword && <FormHelperText error>{errors.currentPassword}</FormHelperText>}
                </Stack>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="newPassword">Nueva contraseña</InputLabel>
                  <OutlinedInput
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    fullWidth
                    value={values.newPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.newPassword && errors.newPassword)}
                  />
                  {touched.newPassword && errors.newPassword && <FormHelperText error>{errors.newPassword}</FormHelperText>}
                </Stack>
                <Stack sx={{ gap: 1 }}>
                  <InputLabel htmlFor="confirmPassword">Confirmar nueva contraseña</InputLabel>
                  <OutlinedInput
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    fullWidth
                    value={values.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  />
                  {touched.confirmPassword && errors.confirmPassword && <FormHelperText error>{errors.confirmPassword}</FormHelperText>}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleClose(resetForm)}>Cerrar</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                Guardar
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
